/**
 * Activity — queue + sessions (design/project/lib/dash2.jsx Activity +
 * dash-desktop.jsx DQueue/DSessions, one responsive DOM with the phone
 * segmented control on both widths). Queue keeps the run-now composer
 * (POST /jobs with phase-6 mode/loopInterval) and adds per-row retry for
 * failed jobs; Sessions keeps status, tokens, and the needs-you badge.
 */
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  PlanLimitError,
  createJob,
  isValidModel,
  type Agent,
  type Effort,
  type Job,
  type RunMode,
  type Session,
} from "../lib/api";
import { timeAgo } from "../lib/format";
import { Card, StatusPill, MonoChip, Btn, Toggle, Icon, I } from "../kit";
import { EmptyState } from "./DashShell";
import { ModelEffortFields } from "./ModelEffortFields";
import { PlanLimitNote } from "./PlanLimitNote";
import { SessionList } from "./SessionList";

const PROMPT_SNIPPET = 110;

function snippet(text: string): string {
  return text.length > PROMPT_SNIPPET
    ? `${text.slice(0, PROMPT_SNIPPET)}…`
    : text;
}

const MODES: [RunMode, string][] = [
  ["message", "Message"],
  ["goal", "Goal"],
  ["loop", "Loop"],
];

export function ActivityTab({
  jobs,
  sessions,
  agents,
  jobsErr,
  sessionsErr,
  machineName,
  onJobsChanged,
  notify,
}: {
  jobs: Job[] | null;
  sessions: Session[] | null;
  agents: Agent[] | null;
  jobsErr: string | null;
  sessionsErr: string | null;
  machineName: (machineId: string) => string;
  onJobsChanged: () => void;
  notify: (message: string) => void;
}) {
  const [tab, setTab] = useState<"queue" | "sessions">("queue");

  // run-now composer (POST /jobs, source "manual")
  const [agentName, setAgentName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [mode, setMode] = useState<RunMode>("message");
  const [loopInterval, setLoopInterval] = useState("");
  // Phase 9 §C per-job override ("" = inherit: agent default, then none).
  const [model, setModel] = useState("");
  const [effort, setEffort] = useState<"" | Effort>("");
  // Session-autoClose per-job override. Run-now's source default is CLOSE
  // (a one-shot you didn't wait for), so this toggle defaults ON. Toggling
  // it off sends an explicit autoClose:false to keep the session warm,
  // overriding the agent default and the source default.
  const [autoClose, setAutoClose] = useState(true);
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  // Phase 10 §C: POST /jobs answers 402 plan_limit when goal/loop modes
  // need Pro — render the inline upgrade note (also set by retrying a
  // failed goal/loop job).
  const [planErr, setPlanErr] = useState<PlanLimitError | null>(null);

  // per-row retry of failed jobs (re-dispatch same agent/prompt/mode)
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!agentName || !prompt.trim()) {
      setFormErr("agent and prompt are required");
      return;
    }
    // Contract §C: presets or a raw model id (/^[a-z0-9.-]{3,64}$/).
    if (model && !isValidModel(model)) {
      setFormErr(
        "model must be opus/sonnet/haiku or a raw id (a-z, 0-9, dot, hyphen)"
      );
      return;
    }
    setBusy(true);
    setFormErr(null);
    setPlanErr(null);
    try {
      const job = await createJob(agentName, prompt.trim(), {
        mode,
        loopInterval: loopInterval.trim() || undefined,
        // Phase 9 §C — omitted when "inherit" (createJob drops empties).
        model: model || undefined,
        effort: effort || undefined,
        // Session-autoClose — explicit boolean override for this run.
        autoClose,
      });
      setPrompt("");
      setMode("message");
      setLoopInterval("");
      setModel("");
      setEffort("");
      setAutoClose(true);
      notify(
        job.state === "queued"
          ? "Queued — machine offline, retried for up to 24h"
          : `Delivered to ${job.agentName}`
      );
      onJobsChanged();
    } catch (e2) {
      if (e2 instanceof PlanLimitError) {
        setPlanErr(e2); // inline upgrade note, no modal
      } else {
        setFormErr(e2 instanceof Error ? e2.message : "run failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const retry = async (j: Job) => {
    if (retryingId) return;
    setRetryingId(j.jobId);
    setPlanErr(null);
    try {
      await createJob(j.agentName, j.prompt, {
        mode: j.mode,
        loopInterval: j.loopInterval,
        // Phase 9 §C: a retry keeps the failed job's assignment.
        model: j.model,
        effort: j.effort,
      });
      notify("Job re-dispatched");
      onJobsChanged();
    } catch (e) {
      if (e instanceof PlanLimitError) {
        setPlanErr(e); // a failed goal/loop job re-dispatch can 402 too
      } else {
        notify(e instanceof Error ? e.message : "retry failed");
      }
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <div>
      <div className="dsh-subtabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "queue"}
          className={tab === "queue" ? "on" : undefined}
          onClick={() => setTab("queue")}
        >
          Queue
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "sessions"}
          className={tab === "sessions" ? "on" : undefined}
          onClick={() => setTab("sessions")}
        >
          Sessions
        </button>
      </div>

      {tab === "queue" ? (
        <div className="dsh-stack" style={{ gap: 14 }}>
          {/* run-now composer */}
          <Card>
            <form className="dsh-composer" onSubmit={submit}>
              <div className="dsh-composer-grid">
                <div>
                  <span className="dsh-field-label">Agent</span>
                  <select
                    className="input"
                    value={agentName}
                    onChange={(e) => setAgentName(e.target.value)}
                  >
                    <option value="" disabled>
                      {agents && agents.length > 0
                        ? "select agent…"
                        : "create an agent first"}
                    </option>
                    {(agents ?? []).map((a) => (
                      <option key={a.agentName} value={a.agentName}>
                        {a.agentName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <span className="dsh-field-label">Prompt</span>
                  <input
                    className="input"
                    placeholder="One-off task for the agent…"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <span className="dsh-field-label">Mode</span>
                <div className="dsh-seg cols-3">
                  {MODES.map(([k, label]) => (
                    <button
                      key={k}
                      type="button"
                      className={mode === k ? "on" : undefined}
                      onClick={() => setMode(k)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {mode === "loop" && (
                  <input
                    className="input mono"
                    style={{ marginTop: 10 }}
                    placeholder="interval, e.g. 10m — empty lets Claude Code self-pace"
                    value={loopInterval}
                    onChange={(e) => setLoopInterval(e.target.value)}
                  />
                )}
              </div>
              {/* phase 9 §C: per-job override — beats the agent default */}
              <ModelEffortFields
                model={model}
                effort={effort}
                onModel={setModel}
                onEffort={setEffort}
                hint={`override for this run only — "inherit" uses the agent's default, then Claude Code's own`}
              />
              {/* session-autoClose: per-run override of close-on-complete.
                  Run-now defaults to close (a one-shot); toggle off to keep
                  the session warm. goal/loop runs ignore this — they end on
                  their own terms or via stop. */}
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Toggle
                  on={autoClose}
                  onClick={() => setAutoClose((v) => !v)}
                  aria-label={
                    autoClose ? "disable auto-close" : "enable auto-close"
                  }
                />
                <span style={{ fontSize: 13, color: "var(--color-fg-2)" }}>
                  Close session when the task finishes (start fresh next run)
                  <span
                    className="hint"
                    style={{ display: "block", fontSize: 12 }}
                  >
                    run-now closes by default — turn off to keep the session
                    warm for a follow-up. Goal and loop runs ignore this
                  </span>
                </span>
              </div>
              <div className="dsh-form-actions">
                <Btn
                  variant="primary"
                  type="submit"
                  disabled={busy || !agentName || !prompt.trim()}
                >
                  <Icon d={I.play} size={14} />{" "}
                  {busy ? "Dispatching…" : "Run now"}
                </Btn>
                {formErr && <span className="dsh-hint-error">{formErr}</span>}
              </div>
              {planErr && <PlanLimitNote err={planErr} />}
            </form>
          </Card>

          {jobsErr && (
            <p className="dsh-hint-error">Couldn't load jobs ({jobsErr})</p>
          )}
          {jobs === null ? (
            !jobsErr && <p className="hint">Loading jobs…</p>
          ) : jobs.length === 0 ? (
            <Card>
              <EmptyState
                icon={I.signal}
                title="Nothing has run yet"
                body="Jobs appear when a schedule fires or you run an agent manually. Offline machines hold jobs queued until they reconnect."
              />
            </Card>
          ) : (
            <div className="dsh-cards">
              {jobs.map((j) => (
                <Card key={j.jobId} pad={14}>
                  <div className="dsh-job-head">
                    <span className="agent">{j.agentName}</span>
                    <MonoChip tone="ghost">{j.source}</MonoChip>
                    {j.mode && j.mode !== "message" && (
                      <span title="delivery mode (phase 6)">
                        <MonoChip tone="accent">
                          {j.mode}
                          {j.mode === "loop" && j.loopInterval
                            ? ` ${j.loopInterval}`
                            : ""}
                        </MonoChip>
                      </span>
                    )}
                    {/* phase 9 §C/§B2: assignment chips where set */}
                    {j.model && (
                      <span title="model override (phase 9)">
                        <MonoChip tone="ghost">{j.model}</MonoChip>
                      </span>
                    )}
                    {j.effort && (
                      <span title="effort override (phase 9)">
                        <MonoChip tone="ghost">effort {j.effort}</MonoChip>
                      </span>
                    )}
                    {j.alwaysRun && (
                      <span title="exempt from budget holds (phase 9 §B2)">
                        <MonoChip tone="ghost">always run</MonoChip>
                      </span>
                    )}
                    {j.autoClose && (
                      <span title="closes its session when the task finishes (start fresh next run)">
                        <MonoChip tone="ghost">auto-close</MonoChip>
                      </span>
                    )}
                    <span className="right">
                      {/* phase 9 §D: why the dispatch gate is holding this
                          job — "waiting: budget" / "waiting: seat limit" */}
                      {j.state === "queued" && j.holdReason && (
                        <span
                          title={
                            j.holdReason === "budget"
                              ? "the night window's token cap is reached — re-dispatched when the gate passes (outside the window, under cap, or cap raised)"
                              : "the target machine's Claude seat reported a usage limit — re-dispatched when the seat recovers"
                          }
                        >
                          <StatusPill status={`hold-${j.holdReason}`} />
                        </span>
                      )}
                      <StatusPill status={j.state} />
                    </span>
                  </div>
                  <div className="dsh-job-prompt" title={j.prompt}>
                    {snippet(j.prompt)}
                  </div>
                  {j.error && (
                    <div className="dsh-job-err">
                      {j.error}
                      {typeof j.attempts === "number" &&
                        ` · ${j.attempts} attempt${j.attempts === 1 ? "" : "s"}`}
                    </div>
                  )}
                  <div className="dsh-job-foot">
                    <span className="when">
                      created {timeAgo(j.createdAt)}
                      {j.deliveredAt &&
                        ` · delivered ${timeAgo(j.deliveredAt)}`}
                    </span>
                    <span className="right">
                      {j.state === "failed" && (
                        <button
                          type="button"
                          className="dsh-mini-btn"
                          disabled={retryingId === j.jobId}
                          onClick={() => retry(j)}
                        >
                          <Icon d={I.refresh} size={12} />{" "}
                          {retryingId === j.jobId ? "retrying…" : "retry"}
                        </button>
                      )}
                      {j.sessionId && (
                        <Link
                          className="dsh-mini-btn accent"
                          to={`/sessions/${j.sessionId}`}
                        >
                          session →
                        </Link>
                      )}
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="dsh-stack" style={{ gap: 14 }}>
          {sessionsErr && (
            <p className="dsh-hint-error">
              Couldn't load sessions ({sessionsErr})
            </p>
          )}
          {sessions === null ? (
            !sessionsErr && <p className="hint">Loading sessions…</p>
          ) : sessions.length === 0 ? (
            <Card>
              <EmptyState
                icon={I.signal}
                title="No sessions yet"
                body="That's either peaceful or broken. Schedule a job and check back at sunrise — transcripts land here."
              />
            </Card>
          ) : (
            <Card pad={0}>
              <SessionList sessions={sessions} machineName={machineName} />
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
